import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { ComplianceReportClient } from '../../api/compliance-report-client.service';
import { ComplianceReportRequirementDto } from '../../api/models/compliance-report-requirement-dto.model';
import {
    getAutomatchData,
    getAutomatchDataFailure,
    getAutomatchDataSuccess,
    getAvailableStatuses,
    getAvailableStatusesFailure,
    getAvailableStatusesSuccess,
    getCurrentContract,
    getCurrentContractFailure,
    getCurrentContractSuccess,
    getCurrentPermissions,
    getCurrentPermissionsFailure,
    getCurrentPermissionsSuccess,
    getJobRequirementsChanges,
    getJobRequirementsChangesFailure,
    getJobRequirementsChangesSuccess,
    getReadinessStatuses,
    getReadinessStatusesFailure,
    getReadinessStatusesSuccess,
    qualifyRequirements,
    saveRequirements,
    saveRequirementsFailure,
    saveRequirementsSuccess,
    saveSelectedCandidateDocument,
    saveSelectedCandidateDocumentFailure,
    saveSelectedCandidateDocumentSuccess,
    updateComplianceNoteForRequirement,
    updateComplianceNoteForRequirementFailure,
    updateComplianceNoteForRequirementSuccess,
    updateComplianceReportNote,
    updateComplianceReportNoteFailure,
    updateComplianceReportNoteSuccess,
    updateContractComplianceStatus,
    updateCurrentComplianceStatus,
    updateRequirementsForCurrentContract,
    updateRequirementsForCurrentContractFailure,
    updateRequirementsForCurrentContractSuccess,
    automatchSignalReceived,
    setSingleSignalAutomatchSuccess,
    updateAyaClearedStatusForCurrentContractSuccess,
    updateAyaClearedStatusForCurrentContractFailure,
    updateAyaClearedStatusForCurrentContract,
    updateClientClearedStatusForCurrentContract,
    updateClientClearedStatusForCurrentContractSuccess,
    updateClientClearedStatusForCurrentContractFailure,
    markAsReviewed,
    markAsReviewedSuccess,
    markAsReviewedFailure,
    processSpecialRules,
    processSpecialRulesSuccess,
    processSpecialRulesFailure
} from '../actions/compliance-report-new.actions';
import { JobRequirementsNSwagMigrationService } from '../../api/migration/job-requirements-nswag-migration.service';
import { ComplianceStatusDto } from '../../api/models/compliance-status-dto.model';
import { ComplianceReportHelperService } from '../../compliance-report-new/shared/services/compliance-report-helper.service';
import { ComplianceReportRequirementNoteDto } from '../../api/models/compliance-report-requirement-note-dto.model';
import { Owner } from 'src/app/shared/helpers/owner.decorator';
import { ComplianceClient } from 'src/app/compliance/api/compliance-client.service';
import { ContractsClient } from '../../api/contracts-client.service';
@Owner('Mic Drop')
@Injectable()
export class ComplianceReportNewEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly complianceReportClient: ComplianceReportClient,
        private readonly _contractsClient: ContractsClient,
        private readonly _complianceClient: ComplianceClient,
        private readonly _jobRequirementsService: JobRequirementsNSwagMigrationService,
        private readonly _complianceReportHelperService: ComplianceReportHelperService
    ) {}

    getCurrentContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getCurrentContract),
            switchMap((action) =>
                this.complianceReportClient.getComplianceReport(action.contractId).pipe(
                    switchMap((currentContract) =>
                        of(
                            getCurrentContractSuccess({ currentContract }),
                            updateCurrentComplianceStatus({ complianceStatus: currentContract.complianceStatus })
                        )
                    ),
                    catchError((result) => {
                        return of(getCurrentContractFailure({ contractId: action.contractId, error: result.message }));
                    })
                )
            )
        )
    );

    processSpecialRules$ = createEffect(() =>
        this._actions$.pipe(
            ofType(processSpecialRules),
            switchMap((action) =>
                this.complianceReportClient.processSpecialRules(action.specialRulesAutoMatchRequestDto).pipe(
                    map((SpecialRulesRequirementDto) => processSpecialRulesSuccess({ SpecialRulesRequirementDto })),
                    catchError((result) => {
                        return of(processSpecialRulesFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    getCurrentPermissions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getCurrentPermissions),
            switchMap(() =>
                this.complianceReportClient.permissions().pipe(
                    map((permissions) => getCurrentPermissionsSuccess({ permissions })),
                    catchError((result) => {
                        return of(getCurrentPermissionsFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    updateRequirementsForCurrentContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateRequirementsForCurrentContract),
            switchMap((action) =>
                this.complianceReportClient.getRequirements(action.contractId).pipe(
                    map((requirements) => updateRequirementsForCurrentContractSuccess({ requirements })),
                    catchError((result) => {
                        console.error(result);
                        return of(updateRequirementsForCurrentContractFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    updateAyaClearedStatusForCurrentContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateAyaClearedStatusForCurrentContract),
            switchMap((action) =>
                this.complianceReportClient.updateComplianceReportStatus(action.contractId, action.status).pipe(
                    map((_) =>
                        updateAyaClearedStatusForCurrentContractSuccess({
                            contractId: action.contractId,
                            status: action.status
                        })
                    ),
                    catchError((result) => {
                        return of(
                            updateAyaClearedStatusForCurrentContractFailure({
                                contractId: action.contractId,
                                status: action.status
                            })
                        );
                    })
                )
            )
        )
    );

    updateClientClearedStatusForCurrentContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateClientClearedStatusForCurrentContract),
            switchMap((action) =>
                this.complianceReportClient.updateComplianceReportStatus(action.contractId, action.status).pipe(
                    map((_) =>
                        updateClientClearedStatusForCurrentContractSuccess({
                            contractId: action.contractId,
                            status: action.status
                        })
                    ),
                    catchError((result) => {
                        return of(
                            updateClientClearedStatusForCurrentContractFailure({
                                contractId: action.contractId,
                                status: action.status
                            })
                        );
                    })
                )
            )
        )
    );

    getAvailableStatuses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getAvailableStatuses),
            switchMap(() =>
                this._complianceClient.getAllStatuses().pipe(
                    map((availableStatuses) => getAvailableStatusesSuccess({ availableStatuses })),
                    catchError((result) => {
                        console.error(result);
                        return of(getAvailableStatusesFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    getReadinessStatuses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getReadinessStatuses),
            switchMap(() =>
                this._complianceClient.getReadinessStatuses().pipe(
                    map((readinessStatuses) => getReadinessStatusesSuccess({ readinessStatuses })),
                    catchError((result) => {
                        console.error(result);
                        return of(getReadinessStatusesFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    qualifyRequirements$ = createEffect(() =>
        this._actions$.pipe(
            ofType(qualifyRequirements),
            map((action) =>
                this._complianceReportHelperService.updateRequirementsWithModifiedRequirements(
                    action.contractId,
                    action.requirements,
                    action.modifiedRequirements,
                    action.automatchData
                )
            ),
            map((action) =>
                this._complianceReportHelperService.getComplianceReportRequirementDtoFromQualifyRequirementsActionParameters(
                    action.contractId,
                    action.requirements,
                    action.oldRequirements,
                    action.modifiedRequirements,
                    action.automatchData
                )
            ),
            switchMap((result) =>
                this.complianceReportClient
                    .saveComplianceReportRequirementsNew(result.contractId, true, result.requirements)
                    .pipe(
                        switchMap(() =>
                            of(saveRequirementsSuccess(), getCurrentContract({ contractId: result.contractId }))
                        ),
                        catchError((error) => {
                            console.error(error);
                            return of(saveRequirementsFailure({ error: error.message }));
                        })
                    )
            )
        )
    );

    saveRequirements$ = createEffect(() =>
        this._actions$.pipe(
            ofType(saveRequirements),
            map((action) => {
                return {
                    contractId: action.contractId,
                    requirements: action.requirements?.map((req) => {
                        const selectedCandidateDocId =
                            this._complianceReportHelperService.getSelectedCandidateDocumentId(
                                action.oldRequirements,
                                req
                            );
                        const selectedRequirementId =
                            this._complianceReportHelperService.getRequirementIdFromSelectedDocument(
                                action.oldRequirements,
                                req
                            );
                        const newIsRequiredValue = this._complianceReportHelperService.getIsRequiredValue(
                            action.oldRequirements,
                            req
                        );
                        const oldIsRequiredValue = action.oldRequirements?.find(
                            (oldReq) => oldReq.documentTypeId === req.documentTypeId
                        ).isRequired;

                        const newIsSelectedValue = this._complianceReportHelperService.isNewDocumentBeingSelected(
                            action.oldRequirements,
                            req
                        );
                        const returnValue = {
                            otherRequirementId: selectedRequirementId,
                            documentTypeId: req.documentTypeId,
                            candidateDocumentId: selectedCandidateDocId,
                            selected: newIsSelectedValue,
                            required: newIsRequiredValue,
                            requiresManualReview: req.requiresManualReview,
                            isRequiredByClient: null,
                            overrideLevel: req.overrideLevel,
                            requiredOverridden: req.isRequired !== oldIsRequiredValue
                        } as ComplianceReportRequirementDto;

                        return returnValue;
                    })
                };
            }),
            switchMap((result) =>
                this.complianceReportClient
                    .saveComplianceReportRequirementsNew(result.contractId, true, result.requirements)
                    .pipe(
                        switchMap(() =>
                            of(saveRequirementsSuccess(), getCurrentContract({ contractId: result.contractId }))
                        ),
                        catchError((error) => {
                            console.error(error);
                            return of(saveRequirementsFailure({ error: error.message }));
                        })
                    )
            ),
            catchError((error) => {
                console.error(error);
                return of(saveRequirementsFailure({ error: error.message }));
            })
        )
    );

    saveSelectedCandidateDocument$ = createEffect(() =>
        this._actions$.pipe(
            ofType(saveSelectedCandidateDocument),
            map((action) => {
                return {
                    isQualified: action.isQualified,
                    contractId: action.contractId,
                    requirements: [
                        {
                            otherRequirementId: action.candidateDocument.requirementId,
                            documentTypeId: action.requirement.documentTypeId,
                            candidateDocumentId: action.candidateDocument.candidateDocumentId,
                            selected: action.isSelected,
                            required: action.requirement.isRequired,
                            requiresManualReview: action.requirement.requiresManualReview,
                            isRequiredByClient: null,
                            overrideLevel: action.requirement.overrideLevel
                        } as ComplianceReportRequirementDto
                    ] as ComplianceReportRequirementDto[]
                };
            }),
            switchMap((result) => {
                if (result.isQualified) {
                    return this.complianceReportClient
                        .saveComplianceReportRequirementsNew(result.contractId, false, result.requirements)
                        .pipe(
                            switchMap((result: ComplianceStatusDto) =>
                                of(
                                    saveSelectedCandidateDocumentSuccess(),
                                    updateContractComplianceStatus({ complianceStatus: result.complianceStatusId })
                                )
                            ),
                            catchError((result) => {
                                console.error(result);
                                return of(saveSelectedCandidateDocumentFailure({ error: result.message }));
                            })
                        );
                }
                return of(saveSelectedCandidateDocumentSuccess());
            })
        )
    );

    updateComplianceReportNote$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceReportNote),
            switchMap((action) =>
                this._contractsClient
                    .updateContract({
                        contractIds: [action.contractId],
                        complianceState: {
                            note: action.complianceReportNote as string,
                            isUpdatedNote:
                                (action.complianceReportNote as string) !== null &&
                                (action.complianceReportNote as string) !== undefined
                                    ? true
                                    : false
                        }
                    } as any)
                    .pipe(
                        map(() =>
                            updateComplianceReportNoteSuccess({
                                contractId: action.contractId,
                                note: action.complianceReportNote
                            })
                        ),
                        catchError((result) => {
                            console.error(result);
                            return of(
                                updateComplianceReportNoteFailure({ contractId: action.contractId, error: result })
                            );
                        })
                    )
            )
        )
    );

    updateComplianceNoteForRequirement$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceNoteForRequirement),
            switchMap((action) =>
                this.complianceReportClient
                    .addRequirementComplianceNote(action.contractId, action.requirementId, {
                        note: action.complianceNote || '',
                        documentTypeId: action.documentTypeId
                    })
                    .pipe(
                        map((note: ComplianceReportRequirementNoteDto) =>
                            updateComplianceNoteForRequirementSuccess({
                                requirementId: action.requirementId,
                                documentTypeId: action.documentTypeId,
                                savedComplianceNote: note,
                                addOrEdit: action.addOrEdit
                            })
                        ),
                        catchError((result) => {
                            console.error(result);
                            return of(saveRequirementsFailure({ error: result.message }));
                        })
                    )
            )
        )
    );

    updateAyaClearedStatusForCurrentContractSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateAyaClearedStatusForCurrentContractSuccess),
            map(() => showToasterMessage({ message: 'Successfully updated clearance status' }))
        )
    );

    updateAyaClearedStatusForCurrentContractFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateAyaClearedStatusForCurrentContractFailure),
            map(() => showToasterMessage({ message: 'Failed to Update clearance status' }))
        )
    );

    updateClientClearedStatusForCurrentContractSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateClientClearedStatusForCurrentContractSuccess),
            map(() => showToasterMessage({ message: 'Successfully updated clearance status' }))
        )
    );

    updateClientClearedStatusForCurrentContractFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateClientClearedStatusForCurrentContractFailure),
            map(() => showToasterMessage({ message: 'Failed to Update clearance status' }))
        )
    );

    updateComplianceNoteForRequirementSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceNoteForRequirementSuccess),
            map((action) =>
                showToasterMessage({ message: `Successfully ${(action.addOrEdit || 'Add').toLowerCase()}ed note` })
            )
        )
    );

    saveRequirementsSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(saveRequirementsSuccess),
            map(() => showToasterMessage({ message: 'Successfully Updated Requirements' }))
        )
    );

    updateComplianceReportNoteSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceReportNoteSuccess),
            switchMap((action) =>
                of(
                    getCurrentContract({ contractId: action.contractId }),
                    showToasterMessage({ message: 'Successfully Updated Compliance Report Note' })
                )
            )
        )
    );

    updateComplianceNoteForRequirementFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceNoteForRequirementFailure),
            map((action) => getCurrentContract({ contractId: action.contractId })),
            map(() => showToasterMessage({ message: 'Failed to Update Compliance Note' }))
        )
    );

    saveRequirementsFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(saveRequirementsFailure),
            tap((action) => console.error('Save Requirements Failed: ', action.error)),
            map(() => showToasterMessage({ message: 'Save Requirements Failed' }))
        )
    );

    updateComplianceReportNoteFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceReportNoteFailure),
            switchMap((action) =>
                of(
                    getCurrentContract({ contractId: action.contractId }),
                    showToasterMessage({ message: 'Failed to Update Compliance Report Note' })
                )
            )
        )
    );

    getAutomatchData$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getAutomatchData),
            switchMap((action) =>
                this.complianceReportClient.autoMatchRequirements(action.automatchRequestData).pipe(
                    map((automatchData) => getAutomatchDataSuccess({ automatchData })),
                    catchError((result) => {
                        console.error(result);
                        return of(getAutomatchDataFailure({ error: result }));
                    })
                )
            )
        )
    );

    getJobRequirementsChanges$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getJobRequirementsChanges),
            switchMap((action) =>
                this._jobRequirementsService.getJobRequirementsChanges(action.jobId, action.contractId).pipe(
                    map((jobRequirements) => getJobRequirementsChangesSuccess({ jobRequirements })),
                    catchError((result) => {
                        console.error(result);
                        return of(getJobRequirementsChangesFailure({ error: result }));
                    })
                )
            )
        )
    );

    automatchSignalReceived$ = createEffect(() =>
        this._actions$.pipe(
            ofType(automatchSignalReceived),
            map((action) => setSingleSignalAutomatchSuccess({ automatchData: action.automatchData }))
        )
    );

    markAsReviewed$ = createEffect(() =>
        this._actions$.pipe(
            ofType(markAsReviewed),
            switchMap((action) =>
                this.complianceReportClient.markAsReviewed(action.contractId).pipe(
                    map((status) => markAsReviewedSuccess({ contractId: action.contractId, complianceStatus: status })),
                    catchError((result) => {
                        console.error(result);
                        return of(markAsReviewedFailure({ error: result }));
                    })
                )
            )
        )
    );

    markAsReviewedSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(markAsReviewedSuccess),
            switchMap((action) =>
                of(
                    getCurrentContract({ contractId: action.contractId }),
                    getAvailableStatuses(),
                    showToasterMessage({ message: 'Successfully updated Compliance Status and requirements.' })
                )
            )
        )
    );

    markAsReviewedFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(markAsReviewedFailure),
            tap((action) => console.error('Mark As Reviewed Failed: ', action.error)),
            map(() => showToasterMessage({ message: 'Mark As Reviewed Failed' }))
        )
    );
}
